import {
  buildCollection,
  buildEntityCallbacks,
  buildProperty,
  type EntityCollection,
  type EntityOnPreSaveProps,
  type EntityReference,
} from "@firecms/core";
import { getAccountPath, type AccountCollectionId } from "../utils";
import {
  LocaleName,
  Locales,
  type Locale,
  type LocalizedString,
  buildLocalizedStringProperty,
} from "../localization";
import {
  buildEventEntityActions,
  type EventEntityActionsOptions,
} from "../actions/buildEventEntityActions";
import { buildSpacesPath } from "./spaces";
import { NAV_GROUP_CONTENT_SESSIONS } from "./navigationGroups";
import type { EventStatus } from "../properties/eventStatus";
import { buildEventStatusEnumValues } from "../properties/eventStatus";
import { getAdminT } from "../i18n/getAdminT";
import { getAdminUiLocale } from "../i18n/getAdminUiLocale";
import { AccessCodeOtpField } from "../fields/AccessCodeOtpField";
import { SuggestEventFieldsFromSpaceField } from "../fields/SuggestEventFieldsFromSpaceField";
import { buildEventContentBundleField } from "../actions/buildEventContentBundleField";

export type Event = {
  mainLocale: Locale;

  name: string;
  title: LocalizedString;
  body: LocalizedString;

  createdByUserId?: string;

  space: EntityReference;

  accessCode: string;
  status: EventStatus;
  locked?: boolean;
  statusChangedAt?: Date;
  archivedFromStatus?: EventStatus;
  logo?: string;

  participantSlotCount: number;
  teamSlotCount: number;
  maxMembersPerTeam: number;
  /** Locale stamped on teams created for this session. Absent means `any`. */
  defaultTeamLocale?: "any" | Locale;

  assignmentMode?: "free" | "locked" | "manual";

  /** Latest published offline package (`contentReleases/{version}`). */
  contentReleaseRef?: EntityReference;
};


const onPreSaveEvent = ({
  values,
  status,
  context,
}: EntityOnPreSaveProps<Event>) => {
  const trimmedName = values?.name?.trim();
  if (!trimmedName) {
    throw new Error("Internal name is required.");
  }
  values.name = trimmedName;

  if (status === "new") {
    values.status = "provisioning";
    const uid = context.authController.user?.uid;
    if (uid) {
      values.createdByUserId = uid;
    }
  }
  return values;
};

export const buildEventsPath = (id: AccountCollectionId) =>
  `${getAccountPath(id)}/events`;

const buildEventStatusProperty = (locale = getAdminUiLocale()) =>
  buildProperty({
    name: "Status",
    description: "Lifecycle state of this live session.",
    dataType: "string",
    readOnly: true,
    enumValues: buildEventStatusEnumValues(locale),
  });

const buildAssignmentModeProperty = (locale = getAdminUiLocale()) => {
  const t = getAdminT(locale);
  return buildProperty({
    name: t("assignmentMode.name"),
    description: t("assignmentMode.description"),
    dataType: "string" as const,
    validation: { required: false },
    defaultValue: "free",
    hideFromCollection: true,
    enumValues: {
      free: t("assignmentMode.forEveryone"),
      locked: t("assignmentMode.invitationOnly"),
      manual: t("assignmentMode.approvalOnly"),
    },
  });
};

const eventStatusProperty = buildEventStatusProperty();

const eventProperties = {
  space: buildProperty({
    name: "Learning Path",
    description:
      "Which prepared content this session uses. It determines what appears when participants tap Learn (stories and levels) and Play (levels and scoring).",
    dataType: "reference",
    path: "spaces",
    validation: { required: true },
    Field: SuggestEventFieldsFromSpaceField,
  }),
  mainLocale: buildProperty({
    name: "App default locale",
    description:
      "Language shown in the app when a participant has not chosen another one. Does not change the language of the content you author here.",
    validation: { required: true },
    dataType: "string",
    defaultValue: Locales.ES,
    hideFromCollection: true,
    enumValues: {
      [Locales.ES]: LocaleName[Locales.ES],
      [Locales.PT]: LocaleName[Locales.PT],
      [Locales.EN]: LocaleName[Locales.EN],
      [Locales.FR]: LocaleName[Locales.FR],
    },
  }),
  name: buildProperty({
    name: "Internal name",
    description:
      "A short label only your team sees in the admin panel. Use something unique and easy to recognize (e.g. q4-leadership-buenos-aires). Participants never see this.",
    validation: { required: true },
    dataType: "string",
  }),
  title: buildLocalizedStringProperty({
    name: "Title",
    description:
      "The headline participants see when they open this session in the app. Add at least one language; include every locale your audience may use.",
    dataType: "string",
    validation: { required: true },
    hideFromCollection: true,
  }),
  body: buildLocalizedStringProperty({
    name: "Body",
    dataType: "string",
    markdown: true,
    description:
      "Short description of this session for participants (welcome message, instructions, or context).",
    hideFromCollection: true,
    validation: { required: true },
  }),
  accessCode: buildProperty({
    name: "Access Code",
    description:
      "Code participants type in the app to join this session. Share it only with people who should take part.",
    validation: { required: true, min: 5, max: 5 },
    dataType: "string",
    Field: AccessCodeOtpField,
    hideFromCollection: true,
  }),
  participantSlotCount: buildProperty({
    name: "Participant slots",
    description:
      "Maximum number of people who can join this event. Each person takes one slot; when all slots are filled, nobody else can join.",
    validation: { required: true, min: 1, max: 2000 },
    dataType: "number",
    defaultValue: 100,
  }),
  teamSlotCount: buildProperty({
    name: "Team slots",
    description:
      "Maximum number of teams for this event. Empty teams are created automatically when the event is provisioned.",
    validation: { required: true, min: 1, max: 200 },
    dataType: "number",
    defaultValue: 10,
  }),
  defaultTeamLocale: buildProperty({
    name: "Team language",
    description:
      "Language assigned to every team in this session. With a specific language, only participants who chose it are placed automatically; anyone who chose another language must be assigned by an operator. Any leaves each empty team open until the first participant with a specific language claims it.",
    validation: { required: true },
    dataType: "string",
    defaultValue: "any",
    hideFromCollection: true,
    enumValues: {
      any: "Any",
      [Locales.ES]: LocaleName[Locales.ES],
      [Locales.EN]: LocaleName[Locales.EN],
      [Locales.FR]: LocaleName[Locales.FR],
      [Locales.PT]: LocaleName[Locales.PT],
    },
  }),
  maxMembersPerTeam: buildProperty({
    name: "Max members per team",
    description:
      "Maximum people allowed in a single team. A team is full when it reaches this limit.",
    validation: { required: true, min: 1, max: 15 },
    dataType: "number",
    defaultValue: 8,
    hideFromCollection: true,
  }),
  assignmentMode: buildAssignmentModeProperty(),
  status: eventStatusProperty,
  locked: buildProperty({
    name: "Locked",
    description:
      "When locked, new participants cannot join and organizational changes are blocked. The session stays running.",
    dataType: "boolean",
    defaultValue: false,
  }),
  contentReleaseRef: buildProperty({
    name: "Content release",
    description:
      "Latest offline content package published for the mobile app. Published automatically on create; use Update content after editing the learning path.",
    dataType: "reference",
    path: "contentReleases",
    readOnly: true,
    hideFromCollection: true,
  }),
  logo: buildProperty({
    name: "Banner override",
    description:
      "Starts as the learning path banner. Replace it to use a different image for this session only. Uploads are center-cropped, resized, and saved as JPEG.",
    dataType: "string",
    validation: { required: false },
    hideFromCollection: true,
    storage: {
      storagePath: "images",
      acceptedFiles: ["image/*"],
      maxSize: 1024 * 1024,
      metadata: {
        cacheControl: "max-age=1000000",
      },
      imageResize: {
        maxWidth: 1200,
        maxHeight: 400,
        mode: "cover",
        format: "jpeg",
        quality: 75,
      },
      fileName: (context) => {
        const base =
          context.file.name.replace(/\.[^.]+$/, "") || "event-logo";
        return `${base}.jpg`;
      },
    },
    columnWidth: 400,
  }),
};

const collection: EntityCollection<Event> = {
  id: "events",
  name: "Events",
  description:
    "A live session: participants enter with an access code, join teams, and use the Learn and Play buttons in the mobile app. Here you manage who joined, scores, and the schedule for this session.",
  singularName: "Event",
  path: "events",
  titleProperty: "name",
  previewProperties: ["name", "status", "contentReleasePublish", "space"],
  group: NAV_GROUP_CONTENT_SESSIONS,
  icon: "CalendarMonth",
  initialFilter: {
    status: ["not-in", ["archived", "deleting"]],
  },
  permissions: () => ({
    read: true,
    edit: true,
    create: true,
    delete: false,
  }),
  propertiesOrder: [
    "space",
    "name",
    "title",
    "body",
    "assignmentMode",
    "participantSlotCount",
    "teamSlotCount",
    "defaultTeamLocale",
    "accessCode",
    "logo",
    "status",
    "locked",
    "contentReleasePublish",
  ],
  properties: eventProperties,
  additionalFields: [buildEventContentBundleField()],
};

export const eventsCollection = buildCollection<Event>(collection);

const buildEventsCollectionBase = (
  id: AccountCollectionId,
  actionOptions: Omit<EventEntityActionsOptions, "accountId"> | undefined,
  collectionOverrides: Partial<EntityCollection<Event>>,
  locale = getAdminUiLocale()
) => {
  const spacesPath = buildSpacesPath(id);

  return buildCollection<Event>({
    ...collection,
    ...collectionOverrides,
    path: buildEventsPath(id),
    callbacks: buildEntityCallbacks({
      onPreSave: onPreSaveEvent,
    }),
    properties: {
      ...eventProperties,
      status: buildEventStatusProperty(locale),
      assignmentMode: buildAssignmentModeProperty(locale),
      space: buildProperty({
        name: "Learning Path",
        description:
          "Which prepared content this session uses. It determines what appears when participants tap Learn (stories and levels) and Play (levels and scoring).",
        dataType: "reference",
        path: spacesPath,
        validation: { required: true },
        Field: SuggestEventFieldsFromSpaceField,
      }),
    },
    entityActions: buildEventEntityActions(
      {
        accountId: id.accountId,
        ...actionOptions,
        archivedView: collectionOverrides.id === "archived-events",
      },
      locale
    ),
  });
};

export const buildEventsCollection = (
  id: AccountCollectionId,
  actionOptions?: Omit<EventEntityActionsOptions, "accountId">,
  locale = getAdminUiLocale()
) =>
  buildEventsCollectionBase(id, actionOptions, {
    id: "events",
    name: "Events",
    initialFilter: {
      status: ["not-in", ["archived", "deleting"]],
    },
  }, locale);

export const buildArchivedEventsCollection = (
  id: AccountCollectionId,
  actionOptions?: Omit<EventEntityActionsOptions, "accountId">,
  locale = getAdminUiLocale()
) =>
  buildEventsCollectionBase(id, actionOptions, {
    id: "archived-events",
    name: "Archived events",
    description: "Archived sessions. You can restore them or delete them permanently.",
    singularName: "Archived event",
    group: "Archived content",
    icon: "Inventory2",
    forceFilter: {
      status: ["==", "archived"],
    },
    initialFilter: undefined,
    permissions: () => ({
      read: true,
      edit: true,
      create: false,
      delete: false,
    }),
  }, locale);
